/* @jsx h */
import { defineComponent, h } from "vue";
import type { SignupStep } from "./signupTypes";

type Props = {
  steps: SignupStep[];
  currentStep: number;
  onSelectStep: (index: number) => void;
};

// Vertical progress list. Completed steps are clickable so owners can jump back and edit.
export const SignupStepper = defineComponent<Props>({
  name: "SignupStepper",
  props: ["steps", "currentStep", "onSelectStep"],
  setup(props) {
    return () => (
      <ol class="relative space-y-1">
        {props.steps.map((step, index) => {
          const done = index < props.currentStep;
          const active = index === props.currentStep;
          const isLast = index === props.steps.length - 1;
          return (
            <li key={step.id} class="relative">
              {!isLast && (
                <span
                  aria-hidden="true"
                  class={[
                    "absolute left-[19px] top-11 h-[calc(100%-36px)] w-px",
                    done ? "bg-primary" : "bg-base-300",
                  ]}
                />
              )}
              <button
                type="button"
                disabled={!done}
                aria-current={active ? "step" : undefined}
                onClick={() => props.onSelectStep(index)}
                class={[
                  "flex w-full items-start gap-4 rounded-lg p-1 text-left transition-colors",
                  done && "hover:bg-base-200",
                ]}
              >
                <span
                  class={[
                    "flex h-8 w-8 m-0.5 shrink-0 items-center justify-center rounded-full border text-sm transition-colors",
                    done && "border-primary bg-primary text-primary-content",
                    active &&
                      "border-primary bg-primary/10 text-primary ring-4 ring-primary/15",
                    !done && !active && "border-base-300 text-base-content/40",
                  ]}
                >
                  <span class="material-symbols-outlined text-lg">
                    {done ? "check" : step.icon}
                  </span>
                </span>
                <span class="pb-6 pt-1">
                  <span
                    class={[
                      "block text-sm font-semibold",
                      active
                        ? "text-accent"
                        : done
                          ? "text-base-content"
                          : "text-base-content/50",
                    ]}
                  >
                    {step.label}
                  </span>
                  <span
                    class={[
                      "block text-xs",
                      active ? "text-base-content/70" : "text-base-content/40",
                    ]}
                  >
                    {step.description}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    );
  },
});
